import { SelectProps } from '@amzn/awsui-components-react/polaris/select';

/**
 * Time-of-day options for a picker, generated at runtime rather than translated (cce-7184). `value`
 * is the stable 24-hour "HH:MM" that is stored and compared — the shift pickers store it and
 * getShiftFilterParams (utils/filterUtils) parses it — while `label` is formatted for the active
 * locale, so the picker shows 12h or 24h time per the user's locale.
 *
 * This replaces the hardcoded English `startTimeOptions`/`endTimeOptions` i18next lists, whose
 * "1:00 am" values were both English-only and a poor match for the HH:MM parser (a "1:00 pm" value
 * parsed to hour 1, and "12:00 am" to hour 12).
 *
 * `stepMinutes` sets the granularity: 15 for the quarter-hour shift-creation picker, 30 for the
 * half-hourly search filters. Options span the full day, `00:00` through `24:00 - stepMinutes`
 * (so `23:45` at 15-minute steps, `23:30` at 30). Only these two steps are supported.
 */
export function buildTimeOptions(locale: string, stepMinutes: 15 | 30 = 15): SelectProps.Option[] {
  const format = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
  const count = Math.floor((24 * 60) / stepMinutes);
  return Array.from({ length: count }, (_, i) => {
    const totalMinutes = i * stepMinutes;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const label = format.format(new Date(Date.UTC(2000, 0, 1, h, m)));
    return { label, value };
  });
}
